import { motion } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';

interface ProviderButtonProps {
  provider: string;
  icon: React.ReactNode;
  onClick?: () => void;
  isSubmitting: boolean;
  delay?: number;
  setError: (error: string) => void;
}

const ProviderButton = ({ 
  provider, 
  icon, 
  onClick,
  isSubmitting, 
  delay = 0,
  setError
}: ProviderButtonProps) => {
  const { signInWithGoogle, signInWithMicrosoft } = useAuth();
  
  const handleClick = async () => {
    if (isSubmitting) return;
    
    try {
      if (provider === 'Google') {
        await signInWithGoogle();
      } else if (provider === 'Microsoft') {
        await signInWithMicrosoft();
      } else if (provider === 'Email') {
        setError('Custom email provider integration coming soon!');
      }
      
      // Call the original onClick if provided (for backward compatibility)
      if (onClick) onClick();
    } catch (error) {
      console.error(`Error signing in with ${provider}:`, error);
      setError(error instanceof Error ? error.message : `Failed to sign in with ${provider}`);
    }
  };

  return (
    <motion.button
      onClick={handleClick}
      disabled={isSubmitting}
      className="w-full py-3.5 px-4 rounded-xl flex items-center justify-center bg-gradient-to-r from-[#ff8c42] to-[#ff7a29] text-white font-medium shadow-sm relative overflow-hidden group"
      initial={{ opacity: 0, y: 30, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ 
        delay: delay, 
        duration: 0.4,
        type: "spring",
        damping: 15
      }}
      whileHover={{ 
        boxShadow: "0 0 25px rgba(255, 140, 66, 0.5)",
        scale: 1.02,
        transition: { duration: 0.3, ease: "easeOut" }
      }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Animated glow effect */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-[#ff8c42]/0 via-[#ff8c42]/30 to-[#ff8c42]/0 opacity-0"
        initial={{ opacity: 0, x: "-100%" }}
        whileHover={{ 
          opacity: 0.8, 
          x: "100%",
          transition: { duration: 1.5, repeat: Infinity, repeatType: "loop", ease: "linear" }
        }}
      />
      
      {/* Button content */}
      <div className="relative z-10 flex items-center">
        <div className="bg-white rounded-full p-1.5 mr-3 flex items-center justify-center">
          {icon}
        </div>
        
        <motion.span 
          initial={{ x: -5, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: delay + 0.2 }}
        >
          Sign in with {provider}
        </motion.span>
        
        {isSubmitting && (
          <motion.svg 
            className="ml-3 h-5 w-5 text-white" 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24"
            initial={{ opacity: 0, scale: 0.5, rotate: 0 }}
            animate={{ opacity: 1, scale: 1, rotate: 360 }}
            transition={{ duration: 0.5, repeat: Infinity, ease: "linear" }}
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </motion.svg>
        )}
      </div>
    </motion.button>
  );
};

export default ProviderButton;